/**
 * API настроек графа (settings, agents, prompts).
 * configId — ID конфигурации. Если задан, работа идёт с config.data.
 */
import { apiJson } from './client.js';

export async function getAgencySafeConfig(configId = null) {
  const url = '/admin/graph-settings/agency-safe-config' + (configId != null && configId !== '' ? qs({ config_id: configId }) : '');
  return apiJson(url, {}, 'Failed to load agency safe config');
}

export async function updateAgencySafeConfig(payload = {}, configId = null) {
  const url = '/admin/graph-settings/agency-safe-config' + (configId != null && configId !== '' ? qs({ config_id: configId }) : '');
  return apiJson(
    url,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
    'Failed to save agency safe config',
  );
}

export async function previewCustomQuote(payload = {}, configId = null) {
  const url = '/admin/graph-settings/custom-pricing/quote' + (configId != null && configId !== '' ? qs({ config_id: configId }) : '');
  return apiJson(
    url,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    'Failed to calculate custom quote',
  );
}

export async function copyAgencySafeConfig(payload = {}) {
  return apiJson(
    '/admin/graph-settings/agency-safe-config/copy',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    'Failed to copy agency safe config',
  );
}

function qs(params) {
  const s = new URLSearchParams(params).toString();
  return s ? `?${s}` : '';
}

export async function getGraphSettings(configId = null) {
  const url = '/admin/graph-settings' + (configId != null ? qs({ config_id: configId }) : '');
  return apiJson(url, {}, 'Не удалось загрузить настройки');
}

/** Модели OpenAI с поддержкой async (deprecated: используйте getLLMModels). */
export async function getOpenAIAsyncModels() {
  const res = await apiJson('/admin/graph-settings/openai-async-models', {}, 'Не удалось загрузить список моделей');
  return res?.models ?? [];
}

/** Унифицированный список моделей (OpenAI + Grok) для select. Возвращает { models, provider_errors }. */
export async function getLLMModels() {
  const res = await apiJson('/admin/graph-settings/llm-models', {}, 'Не удалось загрузить список моделей');
  return {
    models: res?.models ?? [],
    providerErrors: res?.provider_errors ?? {},
  }
}

export async function updateSetting(key, value, configId = null) {
  const url = `/admin/graph-settings/settings/${encodeURIComponent(key)}` + (configId != null ? qs({ config_id: configId }) : '');
  const normalizedValue =
    value && typeof value === 'object' ? value :
    (typeof value === 'number' || typeof value === 'boolean') ? value :
    String(value ?? '');
  return apiJson(url, {
    method: 'PUT',
    body: JSON.stringify({ value: normalizedValue }),
  }, 'Не удалось сохранить настройку');
}

export async function getLlmFallbackStatus(configId = null) {
  const url = '/admin/graph-settings/llm-fallback' + (configId != null ? qs({ config_id: configId }) : '');
  return apiJson(url, {}, 'Не удалось загрузить статус fallback');
}

export async function activateLlmFallback(payload = {}, configId = null) {
  const url = '/admin/graph-settings/llm-fallback/activate' + (configId != null ? qs({ config_id: configId }) : '');
  return apiJson(url, {
    method: 'POST',
    body: JSON.stringify(payload),
  }, 'Не удалось включить fallback');
}

export async function clearLlmFallback(configId = null) {
  const url = '/admin/graph-settings/llm-fallback/clear' + (configId != null ? qs({ config_id: configId }) : '');
  return apiJson(url, { method: 'POST' }, 'Не удалось сбросить fallback');
}

export async function updateAgent(agentKey, payload, configId = null) {
  const { openai_model, temperature, top_p, max_tokens, frequency_penalty, presence_penalty, is_enabled } = payload;
  const body = {};
  if (openai_model !== undefined) body.openai_model = openai_model;
  if (temperature !== undefined) body.temperature = temperature;
  if (top_p !== undefined) body.top_p = top_p;
  if (max_tokens !== undefined) body.max_tokens = max_tokens;
  if (frequency_penalty !== undefined) body.frequency_penalty = frequency_penalty;
  if (presence_penalty !== undefined) body.presence_penalty = presence_penalty;
  if (is_enabled !== undefined) body.is_enabled = is_enabled;
  const url = `/admin/graph-settings/agents/${encodeURIComponent(agentKey)}` + (configId != null ? qs({ config_id: configId }) : '');
  return apiJson(url, {
    method: 'PUT',
    body: JSON.stringify(body),
  }, 'Не удалось сохранить агента');
}

export async function getPrompts(configId = null) {
  const url = '/admin/graph-settings/prompts' + (configId != null ? qs({ config_id: configId }) : '');
  return apiJson(url, {}, 'Не удалось загрузить промпты');
}

/** Обновляет промпт. payload: { base, extra } или { prompt_text } (legacy). */
export async function updatePrompt(agentKey, role, payload, configId = null) {
  const url = `/admin/graph-settings/prompts/${encodeURIComponent(agentKey)}/${encodeURIComponent(role)}` + (configId != null ? qs({ config_id: configId }) : '');
  return apiJson(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
  }, 'Не удалось сохранить промпт');
}

/** Переходы Ядро (Core) → Monetization / Message Generator. */
export async function getTransitions(configId = null) {
  const url = '/admin/graph-settings/transitions' + (configId != null ? qs({ config_id: configId }) : '');
  return apiJson(url, {}, 'Не удалось загрузить переходы');
}

export async function updateTransitions(transitions, configId = null) {
  const url = '/admin/graph-settings/transitions' + (configId != null ? qs({ config_id: configId }) : '');
  return apiJson(url, {
    method: 'PUT',
    body: JSON.stringify({ transitions }),
  }, 'Не удалось сохранить переходы');
}

/** Условные промпты агента: доп. текст по условиям на параметрах. */
export async function getConditionalPrompts(agentKey, configId = null) {
  const url = `/admin/graph-settings/conditional-prompts/${encodeURIComponent(agentKey)}` + (configId != null ? qs({ config_id: configId }) : '');
  return apiJson(url, {}, 'Не удалось загрузить условные промпты');
}

export async function updateConditionalPrompts(agentKey, rules, configId = null) {
  const url = `/admin/graph-settings/conditional-prompts/${encodeURIComponent(agentKey)}` + (configId != null ? qs({ config_id: configId }) : '');
  return apiJson(url, {
    method: 'PUT',
    body: JSON.stringify({ rules }),
  }, 'Не удалось сохранить условные промпты');
}

/** L4 variable definitions — переменные для условий (transitions, conditional_prompts). */
export async function getVariableDefinitions(configId = null) {
  const url = '/admin/graph-settings/variable-definitions' + (configId != null ? qs({ config_id: configId }) : '');
  return apiJson(url, {}, 'Не удалось загрузить определения переменных');
}

export async function updateVariableDefinitions(definitions, configId = null) {
  const url = '/admin/graph-settings/variable-definitions' + (configId != null ? qs({ config_id: configId }) : '');
  return apiJson(url, {
    method: 'PUT',
    body: JSON.stringify({ definitions }),
  }, 'Не удалось сохранить определения переменных');
}

/** Proactive follow-up rules: conditions + interval minutes. First match wins. */
export async function getProactiveFollowupRules(configId = null) {
  const url = '/admin/graph-settings/proactive-followup-rules' + (configId != null ? qs({ config_id: configId }) : '');
  return apiJson(url, {}, 'Не удалось загрузить правила follow-up');
}

export async function updateProactiveFollowupRules(rules, configId = null) {
  const url = '/admin/graph-settings/proactive-followup-rules' + (configId != null ? qs({ config_id: configId }) : '');
  return apiJson(url, {
    method: 'PUT',
    body: JSON.stringify({ rules }),
  }, 'Не удалось сохранить правила follow-up');
}
